'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { Newspaper, X } from 'lucide-react';
import type { Blog } from '@/lib/api';
import { ListingThumbnail, LISTING_GRID_SIZES } from './ListingThumbnail';
import { blogPlainText } from '@/lib/blogFormat';

interface BlogsPageProps {
  blogs: Blog[];
  tags?: string[];
}

/** Plain-text preview of the post body for the card. */
function excerpt(body: string | undefined, max = 140): string {
  const text = blogPlainText(body).replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max).replace(/\s+\S*$/, '')}…`;
}

function formatDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const CHIP_BASE =
  'text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors';
const CHIP_ON =
  'bg-brand-orange text-white border-brand-orange';
const CHIP_OFF =
  'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:border-brand-orange/40';

export function BlogsPage({ blogs, tags = [] }: BlogsPageProps) {
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);

  // Categories come from the posts themselves, so the filter never offers an
  // empty category.
  const categories = useMemo(() => {
    const seen = new Map<number, string>();
    for (const blog of blogs) {
      if (blog.category?.categoryid && !seen.has(blog.category.categoryid)) {
        seen.set(blog.category.categoryid, blog.category.categoryname);
      }
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [blogs]);

  const visible = useMemo(() => {
    return blogs.filter((blog) => {
      if (activeCategory !== null && blog.category?.categoryid !== activeCategory) {
        return false;
      }
      if (activeTag !== null) {
        const postTags = (blog.blog_tags ?? []).map((t) => t.toLowerCase());
        if (!postTags.includes(activeTag.toLowerCase())) return false;
      }
      return true;
    });
  }, [blogs, activeCategory, activeTag]);

  const filtering = activeCategory !== null || activeTag !== null;

  return (
    <div className="mt-8">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3 md:mb-4">
          <Newspaper size={20} className="text-brand-dark dark:text-neutral-100" />
          <h1 className="text-lg md:text-xl font-black text-brand-dark dark:text-neutral-100 tracking-tight">
            Blog
          </h1>
          {blogs.length > 0 && (
            <span className="bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full">
              {visible.length} of {blogs.length}
            </span>
          )}
        </div>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          News, guides and buying advice from BuyMe.
        </p>
      </div>

      {/* Filters */}
      {(categories.length > 0 || tags.length > 0) && (
        <div className="mb-8 space-y-3">
          {categories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-400 mr-1">
                Category
              </span>
              <button
                type="button"
                onClick={() => setActiveCategory(null)}
                className={`${CHIP_BASE} ${activeCategory === null ? CHIP_ON : CHIP_OFF}`}
              >
                All
              </button>
              {categories.map(([id, name]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveCategory(activeCategory === id ? null : id)}
                  className={`${CHIP_BASE} ${activeCategory === id ? CHIP_ON : CHIP_OFF}`}
                >
                  {name}
                </button>
              ))}
            </div>
          )}

          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-400 mr-1">
                Tags
              </span>
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                  className={`${CHIP_BASE} ${
                    activeTag?.toLowerCase() === tag.toLowerCase() ? CHIP_ON : CHIP_OFF
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}

          {filtering && (
            <button
              type="button"
              onClick={() => {
                setActiveCategory(null);
                setActiveTag(null);
              }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"
            >
              <X size={13} />
              Clear filters
            </button>
          )}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl px-6 py-12 text-center">
          <Newspaper size={32} className="mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
          <p className="text-neutral-600 dark:text-neutral-400">
            {filtering
              ? 'No posts match these filters.'
              : 'No blog posts have been published yet. Check back soon.'}
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {visible.map((blog) => {
            const date = formatDate(blog.published_at);
            const postTags = blog.blog_tags ?? [];

            return (
              <li key={blog.blogid}>
                <Link
                  href={`/blog/${blog.slug || blog.blogid}`}
                  className="group flex flex-col h-full bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden shadow-sm hover:border-brand-orange/30 hover:shadow-lg transition-all"
                >
                  <div className="relative aspect-[16/9] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-700">
                    {blog.image ? (
                      <ListingThumbnail
                        src={blog.image}
                        alt={blog.image_alt_text || blog.blogtitle}
                        sizes={LISTING_GRID_SIZES}
                        className="object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Newspaper size={28} className="text-neutral-300 dark:text-neutral-600" />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col flex-1 p-4">
                    <div className="flex items-center gap-2 mb-2 text-xs text-neutral-500 dark:text-neutral-400">
                      {blog.category?.categoryname && (
                        <span className="bg-brand-orange/10 text-brand-orange font-semibold px-2 py-0.5 rounded-full">
                          {blog.category.categoryname}
                        </span>
                      )}
                      {date && <time dateTime={blog.published_at ?? undefined}>{date}</time>}
                    </div>

                    <h2 className="font-bold text-brand-dark dark:text-neutral-100 line-clamp-2 group-hover:text-brand-orange transition-colors">
                      {blog.blogtitle}
                    </h2>

                    <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 line-clamp-3">
                      {excerpt(blog.blogbody)}
                    </p>

                    {postTags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {postTags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 px-2 py-0.5 rounded-full"
                          >
                            #{tag}
                          </span>
                        ))}
                        {postTags.length > 3 && (
                          <span className="text-[11px] text-neutral-400">
                            +{postTags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
